/// <reference types="@types/googlemaps" />

import { Component, OnInit, ElementRef, Input, EventEmitter, SimpleChanges, HostListener, Output } from '@angular/core';

import { googleMapsLookinFreshTheme } from './google-maps-themes';
import { InjectCallbackScriptSrc } from '../../helpers/script-injector';


export interface ILatLng {
    lat: number,
    lng: number,
    type: 'marker-image' | 'marker' | 'shape' | 'circle' | 'direction' | 'point',
    data?: string,
    directionsFrom?: boolean;
    id?: string | number;
    hover?: string;
    color?: string;
    referers?: any[];
}

export interface ILatLngInteraction {
    lat: number,
    lng: number,
    point?: ILatLng,
    projection?: google.maps.Point
}

export interface IPadding {
    top?: number;
    left?: number;
    bottom?: number;
    right?: number;
}

@Component({
    selector: 'google-maps',
    templateUrl: './google-maps.component.html',
    styleUrls: ['./google-maps.component.css']
})

export class GoogleMapsComponent implements OnInit {

    @Input() points: ILatLng[] = [];
    @Input('padding') centerPadding: IPadding = { top: 0, left: 0, bottom: 0, right: 0 };
    defaultPadding: IPadding = { top: 20, left: 20, right: 20, bottom: 20 };
    @Input('auto-center') autoCenter: boolean = true;
    @Input('map-type') mapTypeId: "HYBRID" | "ROADMAP" | "SATELLITE" | "TERRAIN" = "SATELLITE";

    @Output('map-click') onMapClick: EventEmitter<ILatLngInteraction> = new EventEmitter<ILatLngInteraction>();
    @Output('map-hover') onMapHover: EventEmitter<ILatLngInteraction> = new EventEmitter<ILatLngInteraction>();
    @Output('marker-click') onMarkerClick: EventEmitter<ILatLngInteraction> = new EventEmitter<ILatLngInteraction>();
    @Output('marker-hover') onMarkerHover: EventEmitter<ILatLngInteraction> = new EventEmitter<ILatLngInteraction>();


    @HostListener('window:resize') private mapResizeBinding() {
        this.setCenter();
    }

    addedPoints: any[] = [];
    directionRoutes: ILatLng[][] = [];
    currentMap: google.maps.Map;
    directionsService: google.maps.DirectionsService;
    private mapElement: HTMLElement;
    private mapIsReady: boolean = false;
    //private infoMapElement: HTMLElement;
    mapOverlay: google.maps.OverlayView;

    constructor(private eleRef: ElementRef) {

    }


    ngOnChanges(changes: SimpleChanges) {
        if (this.mapIsReady) {
            if (changes.points || changes.centerPadding) {
                this.resetMarkers();
            }
            else if (changes.mapTypeId) {
                this.currentMap.setOptions(this.getDefaultMapOptions());
            }
        }
    }

    ngOnInit() {
        var apiKey = "AIzaSyB46Vd3Of1v1CASqixhYLK8SKXjOsFIzw8";

        InjectCallbackScriptSrc("https://maps.googleapis.com/maps/api/js?key=" + apiKey, () => {
            //this.infoMapElement = document.createElement("div");
            //this.infoMapElement.classList.add("info-map-element");
            //this.eleRef.nativeElement.appendChild(this.infoMapElement);
            this.mapElement = this.eleRef.nativeElement.getElementsByClassName("map-container")[0];

            this.directionsService = new google.maps.DirectionsService;
            this.currentMap = new google.maps.Map(this.mapElement, this.getDefaultMapOptions());
            this.setDefaultCenter();

            this.mapIsReady = true;

            this.mapOverlay = new google.maps.OverlayView();
            this.mapOverlay.setMap(this.currentMap);

            google.maps.event.addListenerOnce(this.currentMap, 'idle', () => {
                this.resetMarkers(true);
            });

            google.maps.event.addListener(this.currentMap, 'click', (event) => {
                this.onMapClick.emit({
                    lat: event.latLng.lat(),
                    lng: event.latLng.lng()
                });
            });

            google.maps.event.addListener(this.currentMap, 'mousemove', (event) => {
                this.onMapHover.emit({
                    lat: event.latLng.lat(),
                    lng: event.latLng.lng()
                });
            });
        });

    }

    private getDefaultMapOptions(): google.maps.MapOptions {
        return {
            disableDefaultUI: true,
            styles: googleMapsLookinFreshTheme(),
            disableDoubleClickZoom: true,
            keyboardShortcuts: false,
            draggableCursor: "default", //css cursor property
            mapTypeId: google.maps.MapTypeId[this.mapTypeId]
        };
    }

    private resetMarkers(forced: boolean = false) {
        this.clearMarkers();
        this.directionRoutes = [];

        if (this.points && this.points.length > 0) {
            for (let i = 0; i < this.points.length; i++) {
                const point = this.points[i];

                if (point.type === "marker-image") {
                    this.addImageMarker(point, point.data);
                }
                else if (point.type === "circle") {
                    this.addCircle(point, point.data);
                }
                else if (point.type === "marker") {
                    this.addMarker(point);
                }
                else if (point.type === "shape") {
                    this.addShapeMarker(point, point.color);
                }

                if (point.directionsFrom) {
                    this.addToDirectionRoutes(point);
                }
            }
        }

        for (let i = 0; i < this.directionRoutes.length; i++) {
            this.getDirections(this.directionRoutes[i], i);
        }

        if (forced || this.autoCenter)
            this.setCenter();
    }

    private addToDirectionRoutes(point: ILatLng) {
        for (let dirRoute of this.directionRoutes) {
            if (dirRoute[0].id === point.id) {
                dirRoute.push(point);
                return;
            }
        }

        this.directionRoutes.push([point]);
    }

    private clearMarkers() {
        if (this.addedPoints.length > 0) {
            for (let addedPoint of this.addedPoints) {
                addedPoint.setMap(null);
            }

            this.addedPoints.length = 0;
        }
    }

    private addPolyLine(points: ILatLng[], color: string = "rgba(112, 112, 112, 0.5)") {
        var polyLineOptions = {
            path: points,
            geodesic: true,
            strokeColor: color,
            strokeOpacity: 1.0,
            strokeWeight: 4,
            map: this.currentMap
        }

        var polyline = new google.maps.Polyline(polyLineOptions);

        this.addedPoints.push(polyline);
    }

    private addCircle(point: ILatLng, color: string = "#FF0000") {
        var circleOptions = {
            strokeColor: color,
            strokeOpacity: 1,
            strokeWeight: 2,
            fillColor: color,
            fillOpacity: 1,
            map: this.currentMap,
            center: { lat: point.lat, lng: point.lng },
            radius: 500,
            hover: "hovering"
        }

        var circle = new google.maps.Circle(circleOptions);

        this.addHoverListener(circle, point);
        this.addHoverListener(circle, point);

        this.addedPoints.push(circle);
    }

    private addImageMarker(point: ILatLng, image: string) {
        var markerOptions = {
            position: point,
            map: this.currentMap,
            icon: {
                url: image,
                anchor: new google.maps.Point(14, 49),
            }
        };

        var marker = new google.maps.Marker(markerOptions);
        //marker.setClickable(false);

        this.addClickListener(marker, point);
        this.addHoverListener(marker, point);

        this.addedPoints.push(marker);
    }

    private addMarker(point: ILatLng, fontColor: string = "white") {

        var markerOptions = {
            position: point,
            map: this.currentMap,
            label: {
                color: fontColor,
                fontSize: "14px",
                text: " "
            }
        };

        var marker = new google.maps.Marker(markerOptions);

        this.addedPoints.push(marker);
    }

    private addShapeMarker(point: ILatLng, color: string = "#FF0000") {

        var markerColor = color;

        var markerOptions = {
            position: point,
            map: this.currentMap,
            hover: point.data,
            icon: this.createCustomSymbol(markerColor)
        };

        var marker = new google.maps.Marker(markerOptions);

        this.addedPoints.push(marker);
    }


    private setCenter() {

        if (this.points && this.points.length > 0) {
            var bounds = new google.maps.LatLngBounds();
            for (let point of this.points) {
                bounds.extend(point);
            }
            //this.padBounds(bounds);

            this.currentMap.setZoom(2);

            var fitPropteries: google.maps.Padding = {
                top: this.defaultPadding.top + (this.centerPadding.top || 0),
                left: this.defaultPadding.left + (this.centerPadding.left || 0),
                bottom: this.defaultPadding.bottom + (this.centerPadding.bottom || 0),
                right: this.defaultPadding.right + (this.centerPadding.right || 0)
            };

            this.currentMap.fitBounds(bounds, fitPropteries);
            this.currentMap.panToBounds(bounds, fitPropteries);
            if (this.currentMap.getZoom() > 10) {
                this.currentMap.setZoom(10);
            }
        }
        else {
            this.setDefaultCenter();
        }
    }

    private addClickListener(toListenTo: any, relPoint: ILatLng) {
        google.maps.event.addListener(toListenTo, 'click', (evt) => {
            var point = this.mapOverlay.getProjection().fromLatLngToContainerPixel(evt.latLng);
            this.onMarkerClick.emit({
                lat: evt.latLng.lat(),
                lng: evt.latLng.lng(),
                point: relPoint,
                projection: point
            })
        });
    }

    private addHoverListener(toListenTo: any, relPoint: ILatLng) {
        google.maps.event.addListener(toListenTo, 'mouseover', (evt) => {
            var point = this.mapOverlay.getProjection().fromLatLngToContainerPixel(evt.latLng);
            this.onMarkerHover.emit({
                lat: evt.latLng.lat(),
                lng: evt.latLng.lng(),
                point: relPoint,
                projection: point
            })
        });

        google.maps.event.addListener(toListenTo, 'mousemove', (evt) => {
            var point = this.mapOverlay.getProjection().fromLatLngToContainerPixel(evt.latLng);
            this.onMarkerHover.emit({
                lat: evt.latLng.lat(),
                lng: evt.latLng.lng(),
                point: relPoint,
                projection: point
            })
        });

        google.maps.event.addListener(toListenTo, 'mouseout', (evt) => {
            var point = this.mapOverlay.getProjection().fromLatLngToContainerPixel(evt.latLng);
            this.onMarkerHover.emit({
                lat: evt.latLng.lat(),
                lng: evt.latLng.lng(),
                projection: point
            })
        });
    }

    private createCustomSymbol(color) {
        var width = 20;
        var height = 20;
        var radius = 12.5;

        var canvas, context;

        canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;

        context = canvas.getContext("2d");

        context.clearRect(0, 0, width, height);

        context.fillStyle = color;
        context.strokeStyle = "rgba(0,0,0,0)";

        context.beginPath();
        context.moveTo(radius, 0);
        context.lineTo(width - radius, 0);
        context.quadraticCurveTo(width, 0, width, radius);
        context.lineTo(width, height - radius);
        context.quadraticCurveTo(width, height, width - radius, height);
        context.lineTo(radius, height);
        context.quadraticCurveTo(0, height, 0, height - radius);
        context.lineTo(0, radius);
        context.quadraticCurveTo(0, 0, radius, 0);
        context.closePath();

        context.fill();
        context.stroke();

        return canvas.toDataURL();
    }

    private getDirection(point1: ILatLng, point2: ILatLng) {
        var request: google.maps.DirectionsRequest = {
            origin: point1.lat + "," + point1.lng,
            destination: point2.lat + "," + point2.lng,
            travelMode: google.maps.TravelMode.DRIVING
        };

        this.makeDirectionRequest(request, (coords: ILatLng[]) => {
            this.addPolyLine(coords);
        });
    }

    private getDirections(points: ILatLng[], index?: number) {
        var origin = points[0];
        var destination = points[points.length - 1];

        var request: google.maps.DirectionsRequest = {
            origin: origin.lat + "," + origin.lng,
            destination: destination.lat + "," + destination.lng,
            travelMode: google.maps.TravelMode.DRIVING,
            waypoints: this.toWaypoints(points)
        };

        this.makeDirectionRequest(request, (coords: ILatLng[]) => {
            this.addPolyLine(coords, this.getColorByIndex(index));
        });
    }

    private toWaypoints(points: ILatLng[]): google.maps.DirectionsWaypoint[] {
        var waypoints: google.maps.DirectionsWaypoint[] = [];
        for (let i = 1; i < points.length - 1; i++) {
            const point = points[i];
            waypoints.push({
                location: new google.maps.LatLng(point.lat, point.lng),
                stopover: true
            });
        }

        return waypoints;
    }


    private cachedRequests = [];
    private makeDirectionRequest(request: google.maps.DirectionsRequest, onDoneFunction: Function) {

        this.directionsService.route(request,
            (callback: google.maps.DirectionsResult, status: google.maps.DirectionsStatus) => {
                var result: ILatLng[] = [];
                if (callback && callback.routes && callback.routes[0]) {
                    var route = callback.routes[0];
                    for (let i = 0; i < route.overview_path.length; i++) {
                        const coord = route.overview_path[i];
                        result.push({
                            lat: coord.lat(),
                            lng: coord.lng(),
                            type: "direction"
                        });
                    }
                }
                onDoneFunction(result);
            }
        );
    }

    private animateMapZoomTo(map, targetZoom) {
        var currentZoom = arguments[2] || map.getZoom();
        if (currentZoom != targetZoom) {
            google.maps.event.addListenerOnce(map, 'zoom_changed', function (event) {
                this.animateMapZoomTo(map, targetZoom, currentZoom + (targetZoom > currentZoom ? 1 : -1));
            });
            setTimeout(function () { map.setZoom(currentZoom) }, 80);
        }
    }

    private setDefaultCenter() {
        var denmarkCenter = { lat: 56.2639, lng: 10.5018 };
        var bounds = new google.maps.LatLngBounds(denmarkCenter);
        this.currentMap.fitBounds(bounds);
        this.currentMap.setZoom(8);
    }

    private getColorByIndex(index: number) {
        if (index === 0) {
            return "rgba(78, 103, 107, 0.5)"
        }
        else if (index === 1) {
            return "rgba(107, 78, 106, 0.5)"
        }

        return "rgba(112, 112, 112, 0.5)"
    }
}
